import { useEffect, useId, useRef, useState } from 'react'

/**
 * The three chart helpers from `mock.js`, rebuilt as components.
 *
 * They keep what the design system fixes — teal line, soft gradient fill, hairline
 * `--border` gridlines, tabular mono axis labels, green/red bars for ±P&L — so charts
 * match in both themes without a charting library and without hard-coded colours.
 */

export function LineChart({
  data,
  height = 180,
  color = 'var(--brand-teal)',
  min: forcedMin,
}: {
  data: number[]
  height?: number
  color?: string
  min?: number
}) {
  const gradientId = useId()
  if (data.length < 2) return null

  const w = 600
  const pad = { t: 12, r: 8, b: 20, l: 8 }
  const min = forcedMin ?? Math.min(...data)
  const span = Math.max(...data) - min || 1
  const iw = w - pad.l - pad.r
  const ih = height - pad.t - pad.b

  const pts = data.map((d, i) => [
    pad.l + (i / (data.length - 1)) * iw,
    pad.t + ih - ((d - min) / span) * ih,
  ])
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0]!.toFixed(1)} ${p[1]!.toFixed(1)}`).join(' ')
  const area = `${line} L${pts.at(-1)![0]!.toFixed(1)} ${pad.t + ih} L${pts[0]![0]!.toFixed(1)} ${pad.t + ih} Z`

  return (
    <svg className="chart-svg" viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" height={height} role="img">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.28" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <g className="chart-grid">
        {[0, 1, 2, 3].map((g) => (
          <line key={g} x1={pad.l} y1={pad.t + (g / 3) * ih} x2={w - pad.r} y2={pad.t + (g / 3) * ih} stroke="var(--border)" />
        ))}
      </g>
      <path d={area} fill={`url(#${gradientId})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  )
}

export function BarChart({ data, height = 160 }: { data: { l: string; v: number }[]; height?: number }) {
  if (data.length === 0) return null
  const w = 600
  const pad = 20
  const max = Math.max(...data.map((d) => Math.abs(d.v))) || 1
  const iw = w - pad
  const gap = iw / data.length
  const bw = gap * 0.62
  const zero = height - 20

  return (
    <svg className="chart-svg" viewBox={`0 0 ${w} ${height}`} height={height} role="img">
      <line x1="0" y1={zero} x2={w} y2={zero} stroke="var(--border)" />
      {data.map((d, i) => {
        const bh = (Math.abs(d.v) / max) * (zero - 8)
        const x = pad + i * gap + (gap - bw) / 2
        return (
          <g key={`${d.l}-${i}`}>
            <rect
              x={x}
              y={d.v >= 0 ? zero - bh : zero}
              width={bw}
              height={bh}
              rx="2"
              fill={d.v >= 0 ? 'var(--pos)' : 'var(--neg)'}
            />
            <text x={x + bw / 2} y={height - 6} textAnchor="middle" className="chart-axis">
              {d.l}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

export function Sparkline({ data, color = 'var(--brand-teal)' }: { data: number[]; color?: string }) {
  if (data.length < 2) return null
  const w = 90
  const h = 26
  const min = Math.min(...data)
  const span = Math.max(...data) - min || 1
  const path = data
    .map((d, i) => `${i ? 'L' : 'M'}${((i / (data.length - 1)) * w).toFixed(1)} ${(h - ((d - min) / span) * h).toFixed(1)}`)
    .join(' ')
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img">
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" />
    </svg>
  )
}

/** "3 Oct" — the chart's date labels. */
function shortDay(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

function signedGbp(v: number): string {
  return `${v < 0 ? '−' : v > 0 ? '+' : ''}£${Math.abs(v).toFixed(2)}`
}

/**
 * Running profit and loss over time: one line against a zero baseline, tinted green
 * where the running total is up and red where it is down. A crosshair snaps to the
 * nearest day and reads out that day's total and its own profit.
 *
 * Drawn at the container's real width rather than stretched, so the axis text keeps
 * its shape at any size.
 */
export function PnlChart({
  data,
  height = 240,
}: {
  data: { date: string; profit: number; cumulative: number }[]
  height?: number
}) {
  const clipUp = useId()
  const clipDown = useId()
  const box = useRef<HTMLDivElement>(null)
  const [w, setW] = useState(600)
  const [hover, setHover] = useState<number | null>(null)

  useEffect(() => {
    const el = box.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => setW(Math.max(240, e!.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  if (data.length === 0) return null
  // A single day still draws: as a flat line from zero to that day's total.
  const points = data.length === 1 ? [{ ...data[0]!, cumulative: 0 }, data[0]!] : data

  const pad = { t: 14, r: 12, b: 26, l: 64 }
  const iw = w - pad.l - pad.r
  const ih = height - pad.t - pad.b
  const values = points.map((d) => d.cumulative)
  const hi = Math.max(0, ...values)
  const lo = Math.min(0, ...values)
  const span = hi - lo || 1
  const x = (i: number) => pad.l + (points.length === 1 ? 0 : (i / (points.length - 1)) * iw)
  const y = (v: number) => pad.t + ((hi - v) / span) * ih
  const zero = y(0)

  const line = points.map((d, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(d.cumulative).toFixed(1)}`).join(' ')
  const area = `${line} L${x(points.length - 1).toFixed(1)} ${zero.toFixed(1)} L${x(0).toFixed(1)} ${zero.toFixed(1)} Z`
  const ticks = [...new Set([hi, 0, lo])]
  const labelAt = [0, Math.floor((points.length - 1) / 2), points.length - 1]

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const px = e.clientX - rect.left
    const i = Math.round(((px - pad.l) / iw) * (points.length - 1))
    setHover(Math.min(points.length - 1, Math.max(0, i)))
  }
  const h = hover === null ? null : points[hover]!

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <svg
        width={w}
        height={height}
        role="img"
        aria-label={`Running profit and loss, ${shortDay(points[0]!.date)} to ${shortDay(points.at(-1)!.date)}: ${signedGbp(values.at(-1)!)}`}
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
        style={{ display: 'block', touchAction: 'none' }}
      >
        <defs>
          <clipPath id={clipUp}>
            <rect x={pad.l} y={pad.t} width={iw} height={Math.max(0, zero - pad.t)} />
          </clipPath>
          <clipPath id={clipDown}>
            <rect x={pad.l} y={zero} width={iw} height={Math.max(0, pad.t + ih - zero)} />
          </clipPath>
        </defs>

        {ticks.map((t) => (
          <g key={t}>
            <line x1={pad.l} x2={w - pad.r} y1={y(t)} y2={y(t)} stroke={t === 0 ? 'var(--border-2)' : 'var(--border)'} />
            <text x={pad.l - 8} y={y(t) + 4} textAnchor="end" fontSize="11" fontFamily="var(--mono)" fill="var(--text-3)">
              {signedGbp(t)}
            </text>
          </g>
        ))}
        {[...new Set(labelAt)].map((i) => (
          <text
            key={i}
            x={x(i)}
            y={height - 6}
            textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
            fontSize="11"
            fontFamily="var(--mono)"
            fill="var(--text-3)"
          >
            {shortDay(points[i]!.date)}
          </text>
        ))}

        <path d={area} fill="var(--pos-soft)" clipPath={`url(#${clipUp})`} />
        <path d={area} fill="var(--neg-soft)" clipPath={`url(#${clipDown})`} />
        <path d={line} fill="none" stroke="var(--text-2)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {h && hover !== null && (
          <g pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={pad.t} y2={pad.t + ih} stroke="var(--border-2)" />
            <circle
              cx={x(hover)}
              cy={y(h.cumulative)}
              r="4.5"
              fill={h.cumulative < 0 ? 'var(--neg)' : 'var(--pos)'}
              stroke="var(--surface)"
              strokeWidth="2"
            />
          </g>
        )}
        {/* The whole plot is the hit target, so the reader aims at a day, not at the line. */}
        <rect x={pad.l} y={pad.t} width={iw} height={ih} fill="transparent" />
      </svg>

      {h && hover !== null && (
        <div
          className="chart-tip"
          style={{
            left: Math.min(Math.max(x(hover), 90), w - 90),
            top: 4,
          }}
        >
          <div className="u-mono u-bold">{signedGbp(h.cumulative)}</div>
          <div className="u-faint u-sm">
            {shortDay(h.date)} · day {signedGbp(h.profit)}
          </div>
        </div>
      )}
    </div>
  )
}
